/**
 * Wire types for the Costanza chat backend, normalized to the app's camelCase
 * convention. The raw (snake_case) HTTP shapes live in `api/` and are mapped on
 * the way out, so nothing downstream deals with snake_case.
 */

import type { Jid } from "./xmpp/jid"
import { EVENT_TYPES } from "./xmpp/constants"
import type { ProductTypeIdentifier } from "../graphql"

export type ChatAuth = {
  /**
   * Opaque, short-lived session token. It is the credential for every other
   * chat call — both the bearer for the HTTP API and the XMPP SASL PLAIN
   * password. The platform JWT is never sent again after the exchange.
   */
  sessionToken: string
  /**
   * The caller's opaque durable identity (FAME-443) — the JID localpart and
   * SASL username. NOT the user id: costanza deals in handles
   * everywhere, and user ids come only from `POST /handles/resolve`.
   */
  handle: string
  /** Seconds; currently 3600. */
  expiresIn: number
  /**
   * How long after sending a message its sender may still retract it, in hours
   * (FAME-522). Sent with the session so the "delete for everyone" affordance
   * follows a server config change without a client redeploy — the alternative
   * was hardcoding 24 against a value that lives in costanza's config.yaml.
   *
   * The server is the authority: it evaluates the window against the archive's
   * stored timestamp, while the client can only compare against its own clock.
   * Treat this as what to OFFER, never as what will be allowed.
   */
  retractionWindowHours: number
}

/** Decides how the peer name resolves: user directory vs the groups API. */
export type ConversationType = "direct" | "group"

type BaseLastEvent = {
  /** The actor's handle — resolved through the directory like any peer. */
  by: string
  /** ISO8601. The row already carries its own timestamp; this is the event's. */
  at: string
}

/**
 * The last activity on a group row when it was a system notice rather than a
 * message (FAME-703). Costanza sends it structured — `type` plus the handles
 * involved — instead of a raw sentence, so the client resolves the handles and
 * localizes the preview itself. Mutually exclusive with `lastBody`.
 */
export type LastEvent =
  | (BaseLastEvent & { type: "archived" | "unarchived" })
  | (BaseLastEvent & { type: "renamed"; name: string })
  | (BaseLastEvent & {
      type:
        | "member_added"
        | "member_removed"
        | "member_left"
        | "promoted"
        | "demoted"
      member: string
    })

/** A row from `GET /conversations`, newest-activity first. */
export type Conversation = {
  /** Bare JID of the other party (`{handle}@{companyNameKey}`), or the room JID for groups. */
  peer: Jid
  type: ConversationType
  /**
   * Group display name, hydrated onto the row server-side (from group_config).
   * Always null for direct conversations — their name resolves via the user
   * directory — and null for a group row the store minted from an inbound
   * message, until the controller fetches the group and names it.
   */
  name: string | null
  /** Always false for direct conversations. */
  archived: boolean
  /**
   * The last message's text, truncated server-side (FAME-657). Absent on a row
   * the store minted itself from an inbound message, until the next reseed.
   */
  lastBody?: string
  /**
   * The last message's sender handle, so the preview can mark it — "You: " for
   * your own, the sender's name in a group (FAME-714). Only the live store path
   * knows it; a server reseed carries no author, so it is absent until the next
   * live message lands.
   */
  lastAuthor?: string
  /**
   * The last activity when it was a group event, not a message (FAME-703). The
   * preview renders this over `lastBody` (the two never coexist) but under
   * `lastRetracted`.
   */
  lastEvent?: LastEvent
  /** The preview reads as a stub when set. */
  lastRetracted: boolean
  /** The last message carried an image; with no caption, the preview names it. */
  lastAttachment?: boolean
  /**
   * A group's member handles (FAME-443), for its member faces. Absent for
   * direct conversations, which have only the peer.
   */
  members?: string[]
  /** Stanza-id of the last message; usable as a per-conversation MAM catch-up anchor. */
  lastMessageId: string
  /** Unix-ms; the list is sorted descending by this. */
  lastTimestamp: number
  /** Presence only — there is no count. */
  unread: boolean
  /** The user's Mute: no push, no OS notification, no nav badge. Unread is still kept. */
  muted?: boolean
}

/**
 * Something the server refused, kept until the UI has said so once. The kind
 * is what the reader needs to hear; which stanza carried it is not.
 */
export type ChatRefusal = {
  kind: "send" | "archived-group" | "not-member" | "retract-expired" | "retract"
  peer: Jid
}

/**
 * "failed" is terminal and the others are not: it means the runtime has stopped
 * retrying (a dead platform session, or an unrecoverable stream error) and only
 * a manual reconnect will bring chat back. "disconnected" is the transient rest
 * state between attempts; "reconnecting" is one pending.
 */
export type ConnState =
  | "disconnected"
  | "connecting"
  | "online"
  | "reconnecting"
  | "failed"

export type EventType = (typeof EVENT_TYPES)[number]

export function isEventType(value: string): value is EventType {
  return (EVENT_TYPES as readonly string[]).includes(value)
}

/**
 * Where a message went: the envelope, not the content. For a group delivery
 * `from` is the room, which is also the conversation; for a 1:1 neither side
 * is the peer on its own, since a message you sent from another tab arrives
 * addressed FROM you.
 */
export type MessageRouting = {
  from: Jid
  to: Jid
}

/** XEP-0085: what someone is doing in the composer. Only `composing` means typing. */
export type ChatState = "active" | "composing" | "paused" | "inactive" | "gone"

/** Someone's chat state in a conversation. Only ever live, never in history. */
export type ChatStateNotice = {
  state: ChatState
  /** The sender's costanza handle, off the server's `<author/>` marker. */
  author?: string
  routing: MessageRouting
}

type BaseMessage = {
  /** origin-id (XEP-0359) when present, else the stanza id — stable for dedupe + future deletion. */
  id: string
  /** The room's stanza-id (XEP-0359) for a group message — the id a group retraction names (FAME-685). */
  roomStanzaId?: string
  /**
   * The sender's costanza handle, off the server's `<author/>` marker (resolve
   * via `/handles/resolve` + the directory).
   */
  author?: string
  body?: string
  /** Unix-ms timestamp (stanza delay for archived/offline messages, else receive time). */
  timestamp: number
  routing: MessageRouting
}

/**
 * A learning item referenced by a chat message (FAME-237), off the
 * `<share xmlns="attensi:share:0">` marker. The wire carries only the reference
 * — type + id — which ejabberd and MAM route opaquely; the sender's snapshot is
 * never trusted. The receiver resolves it to a {@link SharedItem} through the
 * authorized endpoint, so access is enforced by the resolver (FAME-453).
 */
export type SharedItemRef = {
  /** Only "product" in MVP; the wire format is extensible. */
  type: "product"
  /** Numeric product id, string-serialized like the GraphQL `Product.id`. */
  id: string
}

/**
 * Resolved shared-item metadata from `GET /api/v3/chat/shared_items`
 * (FAME-453) — present only for an item the caller can open. An id the caller
 * cannot open comes back absent, which the card renders as its no-access state.
 */
export type SharedItem = {
  id: string
  productType: ProductTypeIdentifier
  title: string
  /** Null for an item with no cover image — the card renders a placeholder. */
  thumbnail: string | null
}

export type ChatMessage = BaseMessage & {
  type: "chat"
  /**
   * Retracted (XEP-0424). Set this to true for revertible client-side
   * optimistic retraction — a flag rather than a replacement, so `body`
   * survives and reverting a refused retraction is just clearing it.
   */
  retracted: boolean
  /**
   * The learning item this message shares, if any (FAME-237) — a bare
   * reference, resolved to a card by the receiver. Absent on a plain text
   * message.
   */
  share?: SharedItemRef
  /**
   * The image this message carries, if any. Its size is on the wire so the
   * thread can reserve the box before the image loads; the URL is asked for
   * separately because it expires.
   */
  attachment?: AttachmentRef
  /**
   * Delivery of one of OUR sends, so a failed one shows as failed rather than
   * vanishing. Set only on the optimistic echo: "sending" the moment it lands
   * in the thread, "sent" once the socket takes it, "failed" if it does not.
   * Absent on everything inbound — a message we received simply arrived.
   */
  status?: MessageStatus
}

export type MessageStatus = "sending" | "sent" | "failed"

export type AttachmentRef = { id: string; width: number; height: number }

type BaseEventMessage = BaseMessage & {
  type: "event"
  event: EventType
}

export type SimpleEventMessage = BaseEventMessage & {
  event: "archived" | "unarchived"
}

export type RenamedEventMessage = BaseEventMessage & {
  event: "renamed"
  name: string
}

export type MemberEventMessage = BaseEventMessage & {
  event:
    | "member_added"
    | "member_removed"
    | "member_left"
    | "promoted"
    | "demoted"
  member: string
}

export type EventMessage =
  | SimpleEventMessage
  | RenamedEventMessage
  | MemberEventMessage

/**
 * The REQUEST to delete a message, routed on to recipients live. Not itself a
 * message that was retracted — it names another one — so it carries no
 * `retracted` flag of its own.
 */
export type RetractionMessage = BaseMessage & {
  type: "retraction"
  /** `id` of the message this deletes. */
  retractedMessageId: string
}

/**
 * The marker Costanza wrote over an archived row, met only in history. Its
 * `id` is the message it replaced, so the stub lands in that slot rather than
 * appending as a new row.
 */
export type TombstoneMessage = BaseMessage & {
  type: "tombstone"
  retracted: true
}

export type Message =
  | ChatMessage
  | EventMessage
  | RetractionMessage
  | TombstoneMessage

export type MessageType = Message["type"]

export type ChatGroup = {
  /** The room-JID localpart, `g-<uuid>` — the group's id everywhere. */
  id: string
  name: string
  /** The owner's handle, or `null` for an ownerless group (FAME-574). */
  owner: string | null
  /** Admin handles, excluding the owner (FAME-574). */
  admins: string[]
  /** Member handles, owner and admins included (FAME-443). */
  members: string[]
}

/** A peer's directory profile, as shown in the conversation list and header. */
export type PeerProfile = {
  /** Display name; falls back to the bare user id for unresolved peers. */
  displayName: string
  /** Null means no picture — render an initial instead. */
  avatar: string | null
}
